/**
 * Faceted-search protection.
 *
 * The browse/directory experience has filters (service, location, distance,
 * rating, sort, search text, pagination). Reflecting those in the URL makes
 * them shareable and bookmarkable — which the current dashboard browse page is
 * not — but it also creates a combinatorial explosion of near-duplicate URLs
 * that would otherwise be crawled and indexed.
 *
 * The rule implemented here is intentionally blunt and easy to reason about:
 *
 *   1. Any public SEO URL carrying a query string is `noindex, follow`.
 *   2. Its canonical points at a CLEAN, parameter-free path.
 *   3. When the filter combination has a real curated landing page
 *      (service + state + city), the canonical is PROMOTED to that page
 *      instead of the bare directory — so filter links consolidate signals
 *      onto the page we actually want ranked.
 *   4. `follow` is preserved everywhere so crawlers still traverse filtered
 *      pages to reach stylist profiles.
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

const { ROUTES, normalizePath, canonicalUrl } = require('./urls');
const { resolveState } = require('./states');
const { resolveServiceCategory } = require('./taxonomy');
const { slugify } = require('./slug');
const { ROBOTS_INDEX, ROBOTS_NOINDEX } = require('./eligibility');

/**
 * Facet params the public directory understands. Anything not on this list is
 * treated as an unknown/tracking parameter — still noindex, still canonicalized
 * away, but never promoted into a landing-page canonical.
 */
const KNOWN_FACET_PARAMS = [
  'service',
  'state',
  'city',
  'q',
  'sort',
  'distance',
  'minRating',
  'mobile',
  'page',
];

/**
 * @param {string | URLSearchParams | null | undefined} search
 * @returns {Record<string, string>}
 */
function parseSearch(search) {
  /** @type {Record<string, string>} */
  const out = {};
  if (!search) return out;

  let entries;
  if (typeof search === 'string') {
    entries = new URLSearchParams(search.replace(/^\?/, '')).entries();
  } else if (typeof search.entries === 'function') {
    entries = search.entries();
  } else {
    return out;
  }

  let next = entries.next();
  while (!next.done) {
    const key = next.value[0];
    const value = next.value[1];
    if (value !== '' && value != null && out[key] === undefined) {
      out[key] = String(value);
    }
    next = entries.next();
  }
  return out;
}

/**
 * Can this filter combination be consolidated onto a curated landing page?
 * Only exact, unambiguous combinations qualify — a location + service filter
 * with nothing else noisy attached.
 *
 * @param {Record<string, string>} params
 * @returns {string | null} the promoted clean path, or null
 */
function promoteToLandingPath(params) {
  const state = resolveState(params.state);
  const category = resolveServiceCategory(params.service);
  const city = params.city ? slugify(params.city) : '';

  // Filters that make the result set narrower than any landing page can
  // legitimately represent — never promote through them.
  const hasNarrowingFilters = Boolean(
    params.q || params.distance || params.minRating || params.mobile || params.sort
  );
  if (hasNarrowingFilters) return null;

  if (state && city && category) return ROUTES.cityService(state.slug, city, category.slug);
  if (state && city) return ROUTES.city(state.slug, city);
  if (state && !city && !category) return ROUTES.state(state.slug);
  if (!state && !city && category) return ROUTES.service(category.slug);
  return null;
}

/**
 * @typedef {Object} FacetAnalysis
 * @property {string}  canonicalPath  clean, parameter-free path to canonicalize to
 * @property {boolean} indexable
 * @property {string}  robots
 * @property {boolean} promoted       true when canonical was moved to a landing page
 * @property {string[]} appliedFacets  known facet params present on the URL
 * @property {string[]} unknownParams  params we do not recognise (tracking, etc.)
 */

/**
 * @param {string} path
 * @param {string | URLSearchParams | null | undefined} search
 * @param {{ baseIndexable?: boolean }} [options] whether the clean page itself is indexable
 * @returns {FacetAnalysis}
 */
function analyzeFacetedUrl(path, search, options) {
  const opts = options || {};
  const baseIndexable = opts.baseIndexable !== false;
  const cleanPath = normalizePath(path);
  const params = parseSearch(search);
  const keys = Object.keys(params);

  const appliedFacets = keys.filter((k) => KNOWN_FACET_PARAMS.indexOf(k) !== -1);
  const unknownParams = keys.filter((k) => KNOWN_FACET_PARAMS.indexOf(k) === -1);

  if (keys.length === 0) {
    return {
      canonicalPath: cleanPath,
      indexable: baseIndexable,
      robots: baseIndexable ? ROBOTS_INDEX : ROBOTS_NOINDEX,
      promoted: false,
      appliedFacets: [],
      unknownParams: [],
    };
  }

  const promotedPath = unknownParams.length === 0 ? promoteToLandingPath(params) : null;

  return {
    // Rule 2/3: canonical is always parameter-free, promoted when we can.
    canonicalPath: promotedPath ? normalizePath(promotedPath) : cleanPath,
    // Rule 1: a parameterized variant is never itself indexable.
    indexable: false,
    robots: ROBOTS_NOINDEX,
    promoted: Boolean(promotedPath),
    appliedFacets,
    unknownParams,
  };
}

/**
 * Rewrite a page's metadata so a filtered/sorted variant points its canonical
 * at the clean page and drops out of the index.
 *
 * @param {Object} metadata a `PageMetadata` from metadata.js
 * @param {FacetAnalysis} analysis
 * @param {{ environment?: string, siteUrl?: string }} [ctx]
 * @returns {Object} a new metadata object
 */
function applyFacetsToMetadata(metadata, analysis, ctx) {
  if (!metadata || !analysis) return metadata;
  const c = ctx || {};
  const canonical = canonicalUrl(analysis.canonicalPath, c);

  return {
    ...metadata,
    canonicalPath: analysis.canonicalPath,
    canonical,
    indexable: metadata.indexable && analysis.indexable,
    robots: metadata.indexable && analysis.indexable ? ROBOTS_INDEX : ROBOTS_NOINDEX,
    openGraph: { ...metadata.openGraph, url: canonical },
  };
}

module.exports = {
  KNOWN_FACET_PARAMS,
  parseSearch,
  promoteToLandingPath,
  analyzeFacetedUrl,
  applyFacetsToMetadata,
};
